import React from "react";
import { Check, ExternalLink } from "lucide-react";

import { saveDispatchGeotabConfirmedPosition } from "../../api/geotabApi.js";
import useAccess from "../../hooks/useAccess.jsx";
import { reloadDispatchGeotabCheck } from "../../hooks/useDispatchGeotabCheck.js";
import { fmtDistance, fmtDuration, fmtTime } from "../geotab/geotabFormat.js";
import "../geotab/GeotabMap.css";
import { mountPlaceMap } from "./dispatchGeotabPlaceMap.js";
import "./DispatchGeotabPlaceCard.css";

// Le bleu représente le lieu prévu ; le rouge représente l’arrêt réellement observé.
const satellite = ([lat, lon]) => `https://www.google.com/maps/@${lat},${lon},600m/data=!3m1!1e3`;

function PlaceMap({ point, stop, address, seen, distance, replay, onPhase }) {
  const ref = React.useRef(null);
  const view = React.useRef(null);
  const [lat, lon, stopLat, stopLon] = [...point, ...stop];
  React.useEffect(() => {
    view.current = mountPlaceMap(ref.current, { point: [lat, lon], stop: [stopLat, stopLon], address, seen, distance, onPhase });
    view.current?.replay();
    return () => { view.current?.destroy(); view.current = null; };
  }, [lat, lon, stopLat, stopLon, address, seen, distance, onPhase]);
  React.useEffect(() => { view.current?.replay(); }, [replay]);
  return <div ref={ref} className="gg-place-map" data-geotab-place-map="true" role="img" aria-label="Carte satellite : lieu prévu en bleu et arrêt observé en rouge" />;
}

export default function DispatchGeotabPlaceCard({ order, date, onSaved, onBack, saved = "" }) {
  const { hasPermission } = useAccess();
  const [replay, setReplay] = React.useState(0);
  const [phase, setPhase] = React.useState("ready");
  const [notice, setNotice] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const backRef = React.useRef(null);
  React.useEffect(() => { backRef.current?.focus({ preventScroll: true }); }, []);
  const confirmed = order.confirmed && order.status === "found";
  const at = `${fmtTime(order.arrival)}${order.dwell == null ? "" : ` (${fmtDuration(order.dwell)})`}`;
  const save = async (stop) => {
    setBusy(true);
    setError("");
    try {
      const { fiches } = await saveDispatchGeotabConfirmedPosition(order.id, stop);
      await reloadDispatchGeotabCheck(date);
      const message = `${stop ? "Position confirmée" : "Position confirmée retirée"} : fiche${fiches.length > 1 ? "s" : ""} ${fiches.join(", ")}.`;
      setNotice(message);
      onSaved?.(message);
    } catch (exc) {
      setError(exc.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gg-place-card" data-geotab-place-card={order.id}>
      <div className="gg-place-toolbar">
        <button ref={backRef} type="button" className="gg-btn" onClick={onBack}>‹ Liste</button>
        <b>{order.client}</b>
        <span className="gg-place-caption" role="status">{{ ready: "Repérage des deux lieux…", blue: "Lieu prévu · repère bleu", red: "Arrêt réel · repère rouge", distance: "Distance entre les deux lieux", done: confirmed ? "Position confirmée" : `Écart de ${fmtDistance(order.distance_m)} · ${at}`, unavailable: "Carte indisponible" }[phase]}</span>
        <button type="button" className="gg-btn" onClick={() => setReplay((value) => value + 1)}>Rejouer les repères</button>
      </div>
      <PlaceMap replay={replay} onPhase={setPhase} point={order.point} stop={order.stop_point} distance={confirmed ? "" : fmtDistance(order.distance_m)}
        address={confirmed ? `Position confirmée · ${order.client}` : `Adresse : ${order.address || order.client}`}
        seen={`Arrêt ${order.seen_truck} · ${at}`} />
      <div className="gg-place-footer text-[0.8rem]">
        <div className="gg-place-info">
        <p className="gg-place-legend"><span><i />Lieu prévu</span><span><i className="gg-place-red" />Arrêt observé</span></p>
        {confirmed ? (
          <p className="m-0">Les livraisons de ce client sont comparées à sa <b>position confirmée</b>, où le camion s’est arrêté, pas au point de son adresse.</p>
        ) : (
          <>
            <p className="m-0 font-bold">Le camion était-il chez le client ?</p>
            <details className="mt-1"><summary className="cursor-pointer">Comment confirmer ce lieu ?</summary>
            <p className="mb-0 mt-1">Arrêt de {order.seen_truck} à {at}, à {fmtDistance(order.distance_m)} du point de l’adresse dans le SaaS,
              hors du cercle de 1 km où un arrêt compte chez le client. Si la carte montre l’arrêt aux bâtiments ou aux silos du client,
              confirmez ce lieu.</p>
            <p className="mb-0 mt-1 text-slate-500">Le bouton Geotab, Consolidation et le Comparatif compareront ses livraisons à ce lieu,
              journées passées comprises. Les routes du Dispatch ne changent pas.</p></details>
          </>
        )}
        <p className="mb-0 mt-1.5 flex flex-wrap gap-x-3">
          <span className="text-slate-500">Google Maps :</span>
          {[["arrêt du camion", order.stop_point], ...(confirmed ? [] : [["point de l’adresse", order.point]])].map(([label, point]) => (
            <a key={label} href={satellite(point)} target="_blank" rel="noopener noreferrer" className="gg-place-link inline-flex items-center gap-1 font-semibold">
              <ExternalLink aria-hidden="true" className="h-3 w-3" />{label}
            </a>
          ))}
        </p>
        </div>
        {hasPermission("data_manager.client_correspondences.write") ? (
          <button type="button" disabled={busy} onClick={() => save(confirmed ? null : order.stop_point)} data-geotab-place-save={confirmed ? "remove" : "confirm"}
            className={`mt-2 inline-flex items-center gap-1 rounded-[4px] border px-2.5 py-1 font-bold disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent ${confirmed
              ? "border-slate-300 bg-white text-slate-700" : "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"}`}>
            {confirmed ? "Retirer la position confirmée" : <><Check aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.6} />C’est le bon lieu</>}
          </button>
        ) : <p className="mb-0 mt-2 text-slate-500">Confirmer le lieu demande le droit « Modifier les correspondances client ».</p>}
        {notice || saved ? <p className="gg-place-notice" role="status">{notice || saved}</p> : null}
        {error ? <p className="mb-0 mt-1.5 font-semibold text-[#9f2f27]" role="alert">{error}</p> : null}
      </div>
    </div>
  );
}
